import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { WarningMessage } from '@common/components';
import { useWorkspaceDeletionWarnings } from '@/hooks';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '../ui/input';
import { LangContext } from '@/context/LangProvider';
import { Button } from '../ui/button';
import { useMutateDeleteWorkspace } from '@/reactQuery/post';
import { EStatusCode } from '@/enums';
import Routes from '@/router/routes';
import { Project, RenameProps, Workspace } from '@/types';

interface DeleteWorkspaceModalProps {
  currentWorkspaceName: string;
  mergedWorkspaces: Workspace[];
  setIsDeletingWorkspace: (value: boolean) => void;
  isLastOwnedWorkspace: boolean;
  projects: Project[];
}

const defaultValues = {
  name: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Workspace name is required'),
});

function DeleteWorkspace({
  currentWorkspaceName,
  mergedWorkspaces,
  setIsDeletingWorkspace,
  isLastOwnedWorkspace,
  projects,
}: DeleteWorkspaceModalProps) {
  const { lang } = useContext(LangContext);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const navigate = useNavigate();
  const { organization_id, workspace_id } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const { mutateAsync: mutateAsyncDeleteWorkspace } = useMutateDeleteWorkspace(
    organization_id,
    workspace_id,
  );

  const warningMessage = useWorkspaceDeletionWarnings({ projects, isLastOwnedWorkspace });

  const onSubmit: SubmitHandler<RenameProps> = async (data) => {
    if (data?.name !== currentWorkspaceName) {
      toast.error(lang.get('msg.invalidWorkspaceName'));
      return;
    }

    try {
      setIsDeletingWorkspace(true);
      await mutateAsyncDeleteWorkspace({});
      const listWithoutDeletedWorkspace = mergedWorkspaces.filter(
        (item) => item._id !== workspace_id,
      );
      const firstWorkspaceId = listWithoutDeletedWorkspace[0]?._id;
      navigate(`/organization/${organization_id}${Routes.workspace.path}/${firstWorkspaceId}`);
      setOpenModal(false);
      reset();
      toast.success(lang.get('msg.workspaceSuccessfullyDeleted'));
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.status === EStatusCode?.CANT_DELETE_PUBLISHED) {
        toast.error(lang.get('msg.publishedOrPausedDeletingWorkspaceError'));
      } else {
        toast.error(lang.get('msg.errorPleaseTryAgain'));
      }
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  return (
    <AlertDialog open={openModal}>
      <AlertDialogTrigger onClick={() => setOpenModal(true)} asChild>
        <p className="py-1 pl-2 text-sm text-red-600 cursor-pointer hover:bg-secondary-background">
          {lang.get('msg.delete')}
        </p>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-1">
              {lang.get('msg.deleteWorkspace')}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {lang.get('msg.areYouSureYouWantToDeleteWorkspace')}
              <br />
              {lang.get('msg.deleteWorkspaceModalDescription')}
              <span className="font-bold"> {currentWorkspaceName} </span>
              {lang.get('msg.workspace')}
            </p>
            {warningMessage && <WarningMessage> {warningMessage}</WarningMessage>}
            <div className="flex flex-col pt-4 pb-12">
              <label className="relative flex flex-col w-full gap-1">
                <p className="mb-2 text-sm text-dark-text">
                  {lang.get('msg.toProceedPleaseEnterWorkspaceName')}
                </p>
                <Input
                  {...register('name')}
                  placeholder={lang.get('msg.enterWorkspaceName')}
                  className={`w-full ${errors?.name?.message && 'border-red-600/50'}`}
                  disabled={isLastOwnedWorkspace}
                />
                <p
                  className={`text-xs md700:hidden font-medium text-red-600 opacity-0 absolute -bottom-5 ${
                    errors?.name?.message && 'opacity-100'
                  }`}
                >
                  {errors?.name?.message}
                </p>
              </label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700"
              disabled={isLastOwnedWorkspace || isSubmitting}
            >
              {lang.get('msg.submit')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteWorkspace;
